import { outsideInConsentText, OutsideInDomain, type OutsideInConsentState } from '@cyberscore/contracts';
import { Loader2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';
import { useToast } from '../../ui/Toasts';

/** Mailboxes anyone can have: their domain isn't the business's. */
const FREE_MAIL = /^(gmail|googlemail|yahoo|ymail|hotmail|outlook|live|icloud|me|proton|protonmail)\./i;

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' }); // prettier-ignore

/**
 * The internet check (task 46, decision 0052): the owner agrees that the insurer may check the business's
 * domains from the internet, passively: email records, the website's certificate and security headers.
 * Nothing is checked until they agree, and they can withdraw at any time.
 */
export function InternetCheck({ id }: { id: string }) {
  const { tenant, me } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const owner = can(tenant, 'outside_in.consent');
  const key = ['outside-in-consent', tenant?.id, id];
  const q = useQuery({ queryKey: key, queryFn: () => api<OutsideInConsentState>('GET', `/assessments/${id}/outside-in/consent`), enabled: !!tenant }); // prettier-ignore
  const done = (s: OutsideInConsentState) => {
    qc.setQueryData(key, s);
    setEditing(false);
    setAgree(false);
  };
  const give = useMutation({ mutationFn: (domains: string[]) => api<OutsideInConsentState>('POST', `/assessments/${id}/outside-in/consent`, { domains, agree: true }), onSuccess: done }); // prettier-ignore
  const withdraw = useMutation({ mutationFn: () => api<OutsideInConsentState>('POST', `/assessments/${id}/outside-in/consent/withdraw`), onSuccess: done }); // prettier-ignore
  const mailDomain = me?.user.email?.split('@')[1]?.toLowerCase() ?? '';
  const [text, setText] = useState<string | null>(null);
  const [agree, setAgree] = useState(false);
  const [editing, setEditing] = useState(false);

  if (q.isPending) return <p className="muted small">Loading…</p>;
  if (q.isError) return <p className="muted small">The internet check isn’t available right now.</p>;
  const s = q.data;
  const c = s.consent;
  const typed = text ?? (c ? c.domains.join(', ') : FREE_MAIL.test(mailDomain) ? '' : mailDomain);
  const parts = typed
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((d) => OutsideInDomain.safeParse(d));
  const domains = [...new Set(parts.flatMap((p) => (p.success ? [p.data] : [])))];
  const bad = parts.some((p) => !p.success);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    give.mutate(domains, { onError: (err) => toast(err.message, 'bad') });
  };

  if (c && !editing)
    return (
      <div className="stack-s">
        <p className="small"><span className="pill ok small-pill">Agreed</span> {s.insurer} may check <b>{c.domains.join(', ')}</b>. Agreed {day(c.created_at)}{c.from_form ? ' on the form you sent' : c.given_by_name ? ` by ${c.given_by_name}` : ''}.</p>
        <p className="muted small">“{c.consent_text}”</p>
        {owner && (
          <div className="row gap-s">
            <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>Change domains</button>
            <button type="button" className="btn btn-sm btn-ghost" disabled={withdraw.isPending} onClick={() => withdraw.mutate(undefined, { onError: (err) => toast(err.message, 'bad') })}>{withdraw.isPending && <Loader2 size={14} className="spin" />}Withdraw</button>
          </div>
        )}
      </div>
    ); // prettier-ignore

  if (!owner)
    return (
      <p className="muted small">
        Not agreed yet. The business owner can let {s.insurer} check your domains here.
      </p>
    );

  return (
    <form className="stack-s" onSubmit={submit}>
      <p className="muted small">Let {s.insurer} check what anyone can see of your domain: your email protection records (SPF, DKIM, DMARC) and your website’s certificate and security headers. Nothing is scanned or tried, and nothing is checked until you agree.</p>
      <label className="small" htmlFor="ic-domains"><b>Your domains</b></label>
      <input id="ic-domains" className="input" placeholder="yourcompany.com.my" value={typed} onChange={(e) => { setText(e.target.value); setAgree(false); }} />
      {bad && <p className="small bad-t" role="alert">Use domain names only, such as yourcompany.com.my.</p>}
      {domains.length > 0 && !bad && (
        <label className="row gap-s small check-row">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>{outsideInConsentText({ business: s.business, insurer: s.insurer, domains })}</span>
        </label>
      )}
      <div className="row gap-s">
        <button type="submit" className="btn btn-sm btn-primary" disabled={!agree || bad || !domains.length || give.isPending}>{give.isPending && <Loader2 size={14} className="spin" />}Agree</button>
        {editing && <button type="button" className="btn btn-sm btn-ghost" onClick={() => { setEditing(false); setText(null); }}>Cancel</button>}
      </div>
    </form>
  ); // prettier-ignore
}
